import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Formik,
  Form,
  FieldArray,
  Field,
} from 'formik'
import * as Yup from 'yup'
import { Map, List } from 'immutable'
import Col from 'muicss/lib/react/col'
import Row from 'muicss/lib/react/row'
import cloneDeep from 'lodash/cloneDeep'
import Input from 'muicss/lib/react/input'
import {
  createCampaign as createCampaignAction,
  updateCampaign as updateCampaignAction,
  getAllAmbassadorsData,
} from 'services/ambassador/redux/actions'
import Option from 'muicss/lib/react/option'
import Button from 'muicss/lib/react/button'
import { formatInTimeZone } from 'date-fns-tz'
import Select from 'muicss-r18/lib/react/select'
import Container from 'muicss/lib/react/container'
import { getViewRecordsAsList } from 'services/data'
import { selectData } from 'services/data/redux/selectors'
import { dismissModal as dismissModalAction } from 'services/modal/redux/actions'
import { AMBASSADOR_PARTNER_OPTIONS_KEY, AMBASSADOR_CAMPAIGN_DETAILS_STORE_KEY } from 'services/ambassador'
import { MultiSelect } from '../../MultiSelect'

import './index.scss'

// CONSTANTS
const LANDING_PAGES_TYPE_ENUM = [
  'Marketing',
  'Sales',
]

const CAMPAIGN_ADD_LANDING_PAGE_INITIAL_VALUE = {
  url: '',
  type: LANDING_PAGES_TYPE_ENUM[0],
}

const CREATE_CAMPAIGN_INITIAL_VALUES = {
  name: '',
  utmCampaign: '',
  startDate: '',
  endDate: '',
  addLandingPages: [CAMPAIGN_ADD_LANDING_PAGE_INITIAL_VALUE],
  addAmbassadors: [],
}

const CREATE_CAMPAIGN_VALIDATION_SCHEMA = Yup.object()
  .shape({
    name: Yup.string().required('A name for the campaign is required'),
    utmCampaign: Yup.string().required('An utmCampaign for the campaign is required'), // add test to check unique
    startDate: Yup.date().required('Start date is required'),
    endDate: Yup.date().required('End date is required'),
    addLandingPages: Yup.array()
      .of(
        Yup.object().shape({
          url: Yup.string().url('The URL was incorrectly formatted'),
          type: Yup.mixed().oneOf(LANDING_PAGES_TYPE_ENUM),
        }),
      ),
    addAmbassadors: Yup.array(),
  })

const EDIT_CAMPAIGN_VALIDATION_SCHEMA = Yup.object()
  .shape({
    name: Yup.string().required('A name for the campaign is required'),
    utmCampaign: Yup.string().required('An utmCampaign for the campaign is required'), // add test to check unique
    startDate: Yup.date().required('Start date is required'),
    endDate: Yup.date().required('End date is required'),
    landingPages: Yup.array()
      .of(
        Yup.object().shape({
          url: Yup.string().url('The URL was incorrectly formatted'),
          type: Yup.mixed().oneOf(LANDING_PAGES_TYPE_ENUM),
        }),
      ),
    changedLandingPages: Yup.array(),
    removeLandingPages: Yup.array(),
  })

// FUNCTIONS
/**
 * Get initial values for the form when editing
 *
 * @returns {Object} Object with the saved data for the current campaign
 */
function getEditInitialValues () {
  const data = useSelector(selectData)
  const campaignData = data.getIn([AMBASSADOR_CAMPAIGN_DETAILS_STORE_KEY, 'records'], Map())

  const startDate = campaignData.get('startDate', 0)
  const endDate = campaignData.get('endDate', 0)
  const dataLandingPages = campaignData.get('landingPages') || List()
  const landingPages = []

  dataLandingPages.forEach((landing) => {
    landingPages.push({
      url: landing.get('url'),
      type: landing.get('type'),
      uuid: landing.get('uuid'),
    })
  })

  landingPages.push(CAMPAIGN_ADD_LANDING_PAGE_INITIAL_VALUE)

  return {
    uuid: campaignData.get('uuid'),
    name: campaignData.get('name'),
    utmCampaign: campaignData.get('utmCampaign'),
    startDate: formatInTimeZone(Date.parse(startDate), 'UTC', 'yyyy-MM-dd'),
    endDate: formatInTimeZone(Date.parse(endDate), 'UTC', 'yyyy-MM-dd'),
    landingPages,
    changedLandingPages: [],
    removeLandingPages: [],
  }
}

/**
 * Get class for an associated lp item
 *
 * @param {Integer} lpsLength lenght of the field array
 * @param {Integer} index Index of the element in the field array
 * @returns {String} A string containing the classes for a lp item
 */
function getAssociatedLpClassName (isEdit, lpsLength, index) {
  const baseClass = 'edit-or-create-campaign-modal__add-landing-pages-item'
  const isActive = isEdit || (lpsLength === index + 1)

  return `${baseClass} ${baseClass}--${isActive ? 'active' : 'disabled'}`
}

/**
 * Is a url string a valid url?
 *
 * @param {String} url String to compare using a regex
 * @returns {Boolean} A boolean value
 */
function isValidUrl (url) {
  const urlReGex = /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g
  return url.length > 0 && url.match(urlReGex)
}

/**
 * Click in the add button, add another input field or
 * clear the current input if the value is not an url
 *
 * @param {Object} arrayHelpers objetc with the form values, props and functions
 * @param {Integer} index Index of the element in the field array
 */
function onClickAdd (arrayHelpers, index) {
  const { name, form } = arrayHelpers
  const { values, setFieldValue } = form
  const currentLp = values[name][index]
  const hasValidUrl = isValidUrl(currentLp.url)

  if (hasValidUrl) {
    arrayHelpers.push(CAMPAIGN_ADD_LANDING_PAGE_INITIAL_VALUE)
  } else {
    setFieldValue(`addLandingPages.${index}.url`, '')
  }
}

/**
 * Click in the remove button, if an existing lp is removed
 * populate the removeLandingPages array, handle the changes
 *
 * @param {Object} arrayHelpers objetc with the form values, props and functions
 * @param {Integer} index Index of the element in the field array
 */
function onClickRemove (arrayHelpers, index) {
  const { name, form } = arrayHelpers
  const { values } = form
  const { removeLandingPages } = values
  if (values[name][index].uuid) {
    removeLandingPages.push(values[name][index].uuid)
  }

  arrayHelpers.remove(index)
}

/**
 * Get the ambassador select options
 *
 * @returns {Array}
 */
const ambassadorsOptions = (recordOptions) => {
  const data = useSelector(selectData)
  const options = Map(recordOptions)
  const viewInstance = { data, storeKey: AMBASSADOR_PARTNER_OPTIONS_KEY, options }
  const records = getViewRecordsAsList(viewInstance) || List()

  return records.map((partner) => ({
    value: partner.get('uuid'),
    label: partner.get('name'),
  })).toJS()
}

/**
 * Arrange the data before sending the petition to the backend to
 * create a new campaign
 *
 * @param {Object} values Object with all the form values
 * @returns {Object}
 */
function handleCreateSubmitData (values) {
  const newData = cloneDeep(values)

  // remove the last input from the landing pages - not added LP
  newData.addLandingPages.pop()

  // take only the uuid for the ambassadors array
  newData.addAmbassadors = newData.addAmbassadors.map((ambassador) => ambassador.value)

  return newData
}

/**
 * Arrange the data before sending the petition to the backend to
 * update an existing campaign
 *
 * @param {Object} values Object with all the form values
 * @returns {Object}
 */
function handleEditSubmitData (values) {
  const newData = cloneDeep(values)
  const addLandingPages = []
  const updateLandingPages = []

  newData.landingPages.pop()

  newData.landingPages.forEach((landing) => {
    if (newData.removeLandingPages.includes(landing.uuid)) { return }
    if (newData.changedLandingPages.includes(landing.uuid)) { updateLandingPages.push(landing) }
    if (!landing.uuid) { addLandingPages.push(landing) }
  })

  delete newData.landingPages
  delete newData.changedLandingPages

  if (newData.removeLandingPages.length === 0) delete newData.removeLandingPages
  if (updateLandingPages.length > 0) newData.updateLandingPages = updateLandingPages
  if (addLandingPages.length > 0) newData.addLandingPages = addLandingPages

  return newData
}

/**
 * When editing a LP url or type, add the uuid to the changedLandingPages
 * array to keep track of what changed
 *
 * @param {Object} e Object with the data of the change event
 * @param {Integer} index Index for the current LP - LP edited
 * @param {Object} arrayHelpers objetc with the form values, props and functions
 * @param {Boolean} isEdit Is the current modal editing existing data
 * @returns {Object}
 */
function landingFiedlOnChange (e, index, arrayHelpers, isEdit) {
  const { form, name } = arrayHelpers
  const { values, handleChange } = form
  const { changedLandingPages } = values
  const { uuid } = values[name][index]

  if (isEdit
    && values.landingPages[index].uuid
    && !changedLandingPages.includes(uuid)) {
    changedLandingPages.push(values[name][index].uuid)
  }

  handleChange(e)
}

// COMPONENTS
/**
 * Renders the ADD or REMOVE button
 *
 * @param {Object} props the props
 * @param {Integer} props.lpListLength number of lp items
 * @param {Integer} props.index index of the current lp item
 * @param {Function} props.onClickAddFunction function to Add another lp field
 * @param {Function} props.onClickRemoveFunction function to remove the current lp
 * @returns {JSX.Element}
 */
const AddOrRemoveLpButton = (props) => {
  const {
    lpListLength,
    index,
    onClickAddFunction,
    onClickRemoveFunction,
  } = props

  const isAdd = lpListLength === index + 1
  const type = isAdd ? 'add' : 'remove'

  return (
    <div
      className={`edit-or-create-campaign-modal__landing-${type}`}
      onClick={isAdd ? onClickAddFunction : onClickRemoveFunction}
    >
      {type.toUpperCase()}
    </div>
  )
}

/**
 * Renders utmCampaign field when a new campaign is created
 *
 * @param {Object} props the props
 * @param {Object} props.values Object with all the form values
 * @param {Function} props.handleChange
 * @param {Object} props.handleBlur
 * @param {Object} props.touched
 * @returns {JSX.Element}
 */
const CreateUtmCampaignField = (props) => {
  const {
    values,
    handleChange,
    handleBlur,
    touched,
    errors,
  } = props

  return (
    <div>
      <Input
        className="edit-or-create-campaign-modal__utm-campaign"
        label="UTM_CAMPAIGN*"
        name="utmCampaign"
        value={values.utmCampaign}
        onChange={handleChange}
        onBlur={handleBlur}
        invalid={touched.utmCampaign && errors.utmCampaign}
      />
      <span className="edit-or-create-campaign-modal__error">
        Note: utmCampaign can not be changed later
      </span>
      {errors.utmCampaign && (
        <div className="edit-or-create-campaign-modal__error">
          {errors.utmCampaign}
        </div>
      )}
    </div>
  )
}

/**
 * Renders utmCampaign field when a new campaign is edited
 *
 * @param {Object} props the props
 * @param {Object} props.values Object with all the form values
 * @returns {JSX.Element}
 */
const EditUtmCampaignField = (props) => {
  const {
    values,
  } = props

  return (
    <Row>
      <Col sm="12" md="6">
        <div className="custom-label">UTM_CAMPAIGN</div>
        <div className="edit-or-create-campaign-modal__utm-campaign-static">
          {values.utmCampaign}
        </div>
      </Col>
    </Row>
  )
}

/**
 * render landing page type select options
 *
 * @returns {JSX.Element}
 */
const LandingPageTypeOptions = () => LANDING_PAGES_TYPE_ENUM.map((type) => (
  <Option
    className="edit-or-create-campaign-modal__landing-type-option"
    key={type}
    value={type}
    label={type}
  />
))

/**
 * Form to create a new campaign
 *
 * @returns {JSX.Element}
 */
const EditOrCreateCampaignModal = (props) => {
  const { isEdit = false } = props
  const dispatch = useDispatch()

  const recordOptions = {
    perPage: 1000,
    sortField: 'name',
    sortDirection: 'ASC',
    activeOnly: true,
  }

  let title = 'Create New Campaign'
  let fieldArrayname = 'addLandingPages'
  let validationSchema = CREATE_CAMPAIGN_VALIDATION_SCHEMA
  let initialValues = CREATE_CAMPAIGN_INITIAL_VALUES
  let handleSubmitData = handleCreateSubmitData
  let dispathAction = createCampaignAction
  let UtmCampaignComponent = CreateUtmCampaignField

  if (isEdit) {
    title = 'Edit Campaign Info'
    fieldArrayname = 'landingPages'
    validationSchema = EDIT_CAMPAIGN_VALIDATION_SCHEMA
    initialValues = getEditInitialValues()
    handleSubmitData = handleEditSubmitData
    dispathAction = updateCampaignAction
    UtmCampaignComponent = EditUtmCampaignField
  } else {
    useEffect(() => {
      dispatch(getAllAmbassadorsData({
        storeKey: AMBASSADOR_PARTNER_OPTIONS_KEY,
        options: recordOptions,
      }))
    }, [])
  }

  const onDismissHandler = (e) => {
    e.persist()
    dispatch(dismissModalAction())
  }

  const handleSubmit = (values) => {
    const data = handleSubmitData(values)
    dispatch(dispathAction(data))
  }

  return (
    <div className="edit-or-create-campaign-modal">
      <h2 className="edit-or-create-campaign-modal__title">{title}</h2>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        className="edit-or-create-campaign-modal__form"
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
        }) => (
          <Form>
            <Container className="edit-or-create-campaign-modal__container" fluid={true}>
              <Row className="edit-or-create-campaign-modal__row">
                <Col sm="12" md="6">
                  <Input
                    className="edit-or-create-campaign-modal__name"
                    label="Name*"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    invalid={touched.name && errors.name}
                  />
                  <div className="edit-or-create-campaign-modal__error">
                    {errors.name}
                  </div>
                </Col>
                <Col sm="12" md="6">
                  <UtmCampaignComponent
                    values={values}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
              </Row>
              <Row className="edit-or-create-campaign-modal__row">
                <Col sm="12" md="6">
                  <Input type="date"
                    label="Start Date*"
                    className="edit-or-create-campaign-modal__start-date"
                    name="startDate"
                    value={values.startDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    invalid={touched.startDate && errors.startDate}
                  />
                  <div className="edit-or-create-campaign-modal__error">
                    {errors.startDate}
                  </div>
                </Col>
                <Col sm="12" md="6">
                  <Input type="date"
                    label="End Date*"
                    className="edit-or-create-campaign-modal__end-date"
                    name="endDate"
                    value={values.endDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    invalid={touched.endDate && errors.endDate}
                  />
                  <div className="edit-or-create-campaign-modal__error">
                    {errors.endDate}
                  </div>
                </Col>
              </Row>
              <Row className="edit-or-create-campaign-modal__row">
                <Col sm="12" className="edit-or-create-campaign-modal__add-landing-pages">
                  <span className="custom-label">Associated LP&apos;s (Base URL only, no UTM&apos;s)</span>
                  <FieldArray
                    name={fieldArrayname}
                    render={(arrayHelpers) => (
                      <>
                        {values[fieldArrayname].map((lp, index) => (
                          <div
                            className={getAssociatedLpClassName(
                              isEdit,
                              values[fieldArrayname].length,
                              index,
                            )}
                            key={index}
                          >
                            <Row>
                              <Col sm="6">
                                {isEdit || values[fieldArrayname].length === index + 1 ? (
                                  <Input
                                    key="url"
                                    className="edit-or-create-campaign-modal__landing-url"
                                    name={`${fieldArrayname}.${index}.url`}
                                    value={values[fieldArrayname][index].url}
                                    onChange={(e) => landingFiedlOnChange(
                                      e,
                                      index,
                                      arrayHelpers,
                                      isEdit,
                                    )}
                                    onBlur={handleBlur}
                                    invalid={touched[fieldArrayname]
                                      && !!errors[fieldArrayname]
                                      && errors[fieldArrayname][index]
                                    }
                                  />
                                ) : (
                                  <div className="edit-or-create-campaign-modal__added-url">
                                    {values[fieldArrayname][index].url}
                                  </div>
                                )}
                              </Col>
                              <Col sm="3">
                                <Select
                                  className="edit-or-create-campaign-modal__landing-type"
                                  name={`${fieldArrayname}.${index}.type`}
                                  onChange={(e) => landingFiedlOnChange(
                                    e,
                                    index,
                                    arrayHelpers,
                                    isEdit,
                                  )}
                                  value={values[fieldArrayname][index].type}
                                >
                                  {LandingPageTypeOptions()}
                                </Select>
                              </Col>
                              <Col sm="3">
                                <AddOrRemoveLpButton
                                  lpListLength={values[fieldArrayname].length}
                                  index={index}
                                  onClickAddFunction={() => onClickAdd(arrayHelpers, index)}
                                  onClickRemoveFunction={() => onClickRemove(arrayHelpers, index)}
                                />
                              </Col>
                            </Row>
                            <div className="edit-or-create-campaign-modal__error">
                              {!!errors[fieldArrayname] && errors[fieldArrayname][index]?.url}
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  />
                </Col>
              </Row>
              {!isEdit && (
                <Row className="edit-or-create-campaign-modal__row">
                  <Col sm="12" md="12">
                    <span className="custom-label">Partners</span>
                    <Field
                      component={MultiSelect}
                      name="addAmbassadors"
                      options={ambassadorsOptions(recordOptions)}
                      placeholder="Add a partner"
                      invalid={touched.addAmbassadors && errors.addAmbassadors}
                    />
                    <div className="edit-or-create-campaign-modal__error">
                      {errors.addAmbassadors}
                    </div>
                  </Col>
                </Row>
              )}
              <div className="edit-or-create-campaign-modal__action-buttons">
                <div>
                  <div>
                    <Button
                      variant="raised"
                      type="submit"
                    >
                      SAVE
                    </Button>
                  </div>
                  <div>
                    <Button
                      type="button"
                      variant="raised"
                      color="accent"
                      className="mui-btn--secondary"
                      onClick={onDismissHandler}
                    >
                      CANCEL
                    </Button>
                  </div>
                </div>
              </div>
            </Container>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default (EditOrCreateCampaignModal)
